import React, { useState, useEffect } from 'react'

import { SharedActionCreators, sharedActionCreators } from 'containers/shared'
import { connect } from 'containers/store'

import { PrimaryNavigationEditEntity } from 'components/ui/primary-navigation/edit-entity'
import { SecondaryNavigation } from 'components/ui/secondary-navigation'
import { StatusGuard } from 'components/ui/status-guard'

import { actionCreators, GettyActionCreators } from 'domains/org/getty/actions'
import { GettyStore, GettyAppState } from 'domains/org/getty/store'

import { pick } from 'helpers/core'
import { statusSelector } from 'helpers/status'
import { space } from 'helpers/style'
import { timestampToFormattedString } from 'helpers/timestamps'

import { Page } from "generated/mos/pagination"
import { Release } from 'generated/mos/org/getty/publish'
import { ListReleasesRequest } from 'generated/mos/org/getty/publishaccess'

import { LayoutListOrFormPage } from 'layouts/list-or-form-page'
import { BundleList } from './bundleList'

import styled from 'styled'

type ActionProps = Pick<GettyActionCreators, 'gettyGetListReleasesRequest'>

type ConnectedProps = Pick<GettyAppState, 'publishReleases'>

type DirectProps = {
  projectId: string;
  historyPush: (id: string) => void;
  navBackRoute?: string;
};
type Props = ActionProps & ConnectedProps & DirectProps;

const Title = styled.h3`
  color: ${({ theme }) => theme.color.grayD80};
  font-size: ${({ theme }) => theme.font.size.base};
  font-weight: ${({ theme }) => theme.font.weight.base};
  line-height: 1.6;
  margin-bottom: ${space(4)};
`

const Releases = styled.ol`
  padding-left: 0;
`

const ReleasesItem = styled.li`
  list-style: none;
  padding-bottom:${space(8)};
  margin-bottom: ${space(4)};
  border-bottom: 1px solid ${({ theme }) => theme.color.grayL70 };
`
const Notes = styled.div`
  background: ${({ theme }) => theme.color.white};
  border-radius: 2px;
  padding: ${space(4)};
  border: solid 1px ${({ theme }) => theme.color.grayL80};
  box-sizing: border-box;
  color: ${({ theme }) => theme.color.gray};
  font-size: ${({ theme }) => theme.font.size.base};
  font-weight: ${({ theme }) => theme.font.weight.light};
  line-height: 1.2;
`

const NoResults = styled.div`
  text-align: center;
  padding-top: ${space(5)};
  padding-bottom: ${space(5)};
`;

const ReleasesPageView = (props: Props) => {
  const {
    projectId,
    gettyGetListReleasesRequest,
    publishReleases
  } = props


  const data = statusSelector.data(publishReleases)
  const startedAt = (release: Release.Entity): JSX.Element | undefined => {
    return release.metadata &&
      release.metadata.createdAt &&
      (<Title>Started publishing at {timestampToFormattedString(release.metadata.createdAt.value)}</Title>);
  }
  const finishedAt = (release: Release.Entity): JSX.Element | undefined | false => {
    return release.bundles.every(({processingStatus}) => processingStatus === 'BUNDLE_PROCESSING_COMPLETED') &&
      release.metadata &&
      release.metadata.modifiedAt &&
      (<Title>Finished publishing at {timestampToFormattedString(release.metadata.modifiedAt.value)}</Title>);
  }

  useEffect(() => {
    gettyGetListReleasesRequest({
      ...ListReleasesRequest.defaults,
      sortOrder: 'SORT_CREATED_DESC',
      page: {
        ...Page.defaults,
        pageSize: 100,
      }
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps


  return (
    <LayoutListOrFormPage
      nav={() => (
        <PrimaryNavigationEditEntity
          title="Publish Project"
          navBackRoute={`/project/${projectId}/publishing`}
          darkTheme
        />
      )}
      title="All Releases"
      subnav={() => (
        <SecondaryNavigation
          navItems={[]}
        />
      )}
      sidebar={() => null}
      content={() => (
        <StatusGuard status={publishReleases}>
          {data && data.length ?
            <Releases>
              {data && data.map(( release: Release.Entity) => {
                return (
                  <ReleasesItem key={Release.mustRef(release.ref).id}>
                    {startedAt(release)}
                    {finishedAt(release)}
                    <Notes>{release.releaseNotes} <BundleList bundles={release.bundles} /></Notes>
                  </ReleasesItem>
                )
                })}
            </Releases>
          :
            <NoResults>
              <h3>No Releases</h3>
              <p>There has been no releases published.</p>
            </NoResults>
          }
        </StatusGuard>
      )}
    />
  )
}

export const ReleasesPage = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  (store) => pick(store.getty, 'publishReleases'),
  {
    ...pick(actionCreators, 'gettyGetListReleasesRequest'),
  }
)(ReleasesPageView)
